import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Camera, Heart, Users, Briefcase, PartyPopper, Film, ChevronRight, Check } from "lucide-react";
import WhatsAppButton from "@/components/WhatsAppButton";

const services = [
  {
    icon: Heart,
    title: "Wedding Photography",
    description: "Capture the magic of your special day with our artistic wedding photography. From intimate ceremonies to grand celebrations, we document every precious moment with elegance and emotion.",
    features: ["Full-day coverage", "Two photographers", "500+ edited photos", "Online gallery", "Premium album"],
    image: "https://images.unsplash.com/photo-1723816089302-81cb5d2c2644?w=800&q=85",
  },
  {
    icon: Camera,
    title: "Pre-Wedding Shoots",
    description: "Create stunning pre-wedding memories at breathtaking locations. We help couples showcase their love story through creative and romantic photography sessions.",
    features: ["Location scouting", "Styling consultation", "4-6 hour session", "100+ edited photos", "Same-day preview"],
    image: "https://images.unsplash.com/photo-1605368504600-f2cca6ea012a?w=800&q=85",
  },
  {
    icon: Users,
    title: "Portrait Photography",
    description: "Professional portraits that capture personality and emotion. Perfect for individuals, families, and corporate headshots with studio or outdoor settings.",
    features: ["Personal consultation", "Professional lighting", "Multiple outfit changes", "Retouched images", "Digital delivery"],
    image: "https://images.unsplash.com/photo-1674123894247-b635dfe990c5?w=800&q=85",
  },
  {
    icon: Briefcase,
    title: "Commercial Photography",
    description: "Elevate your brand with professional commercial photography. From product shots to corporate events, we deliver images that drive business success.",
    features: ["Brand consultation", "Product styling", "High-resolution files", "Commercial license", "Quick turnaround"],
    image: "https://images.unsplash.com/photo-1768818536330-62ae6edf84ba?w=800&q=85",
  },
  {
    icon: PartyPopper,
    title: "Event Photography",
    description: "Document your important events with professional coverage. Birthday celebrations, anniversaries, corporate gatherings - we capture it all.",
    features: ["Full event coverage", "Candid shots", "Group photos", "Quick delivery", "Online sharing"],
    image: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&q=85",
  },
  {
    icon: Film,
    title: "Cinematic Videography",
    description: "Tell your story through stunning cinematic videos. Our videography services bring your moments to life with professional editing and emotional storytelling.",
    features: ["4K filming", "Professional audio", "Cinematic editing", "Highlight reel", "Full ceremony video"],
    image: "https://images.unsplash.com/photo-1669050950123-9d6ff9c88e0a?w=800&q=85",
  },
];

export default function Services() {
  return (
    <main data-testid="services-page" className="pt-24">
      {/* Hero Section */}
      <section data-testid="services-hero" className="section">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-gold text-sm uppercase tracking-[0.3em] mb-4 block">
              What We Offer
            </span>
            <h1 className="font-serif text-5xl md:text-7xl font-medium text-white mb-6">
              Our Services
            </h1>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              From weddings to commercial shoots, we offer comprehensive photography and 
              videography services tailored to your unique needs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section data-testid="services-list" className="pb-24">
        <div className="container-custom">
          <div className="space-y-24">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8 }}
                data-testid={`service-detail-${index}`}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
              >
                <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                  <div className="aspect-[4/3] overflow-hidden relative group">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover transition-transform duration-1200 ease-slow group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-colors duration-500" />
                  </div>
                </div>
                <div className={index % 2 === 1 ? "lg:order-1" : ""}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 bg-gold/10 flex items-center justify-center">
                      <service.icon size={28} strokeWidth={1} className="text-gold" />
                    </div>
                    <span className="text-white/40 text-sm uppercase tracking-widest">Service {String(index + 1).padStart(2, '0')}</span>
                  </div>
                  <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">
                    {service.title}
                  </h2>
                  <p className="text-white/60 text-lg leading-relaxed mb-8">
                    {service.description}
                  </p>
                  <div className="space-y-3 mb-8">
                    {service.features.map((feature) => (
                      <div key={feature} className="flex items-center gap-3">
                        <Check size={16} className="text-gold" />
                        <span className="text-white/70">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Link
                    to="/contact"
                    data-testid={`service-book-${index}`}
                    className="inline-flex items-center gap-2 text-gold text-sm uppercase tracking-widest hover:gap-4 transition-all duration-300"
                  >
                    Book This Service
                    <ChevronRight size={18} strokeWidth={1.5} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing CTA */}
      <section data-testid="services-cta" className="section bg-surface">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-gold text-sm uppercase tracking-[0.3em] mb-4 block">
                Custom Packages
              </span>
              <h2 className="font-serif text-4xl md:text-5xl font-medium text-white mb-6">
                Tailored to Your Needs
              </h2>
              <p className="text-white/60 text-lg leading-relaxed mb-8">
                Every project is unique, and so are our packages. We offer customized solutions 
                based on your specific requirements, timeline, and budget. Let's discuss what 
                works best for you.
              </p>
              <Link
                to="/contact"
                data-testid="services-custom-btn"
                className="inline-flex items-center gap-2 bg-gold text-black px-8 py-4 text-sm uppercase tracking-widest font-bold hover:bg-white transition-all duration-500"
              >
                Get Custom Quote
                <ChevronRight size={18} strokeWidth={1.5} />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-[#050505] border border-white/10 p-8 md:p-12"
            >
              <h3 className="font-serif text-2xl text-white mb-6">What's Included</h3>
              <ul className="space-y-4">
                {[
                  "Pre-shoot consultation",
                  "Professional equipment",
                  "Expert editing & retouching",
                  "High-resolution digital files",
                  "Online gallery for sharing",
                  "Print-ready formats",
                  "Quick turnaround time",
                  "Unlimited revisions",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-white/70">
                    <Check size={16} className="text-gold flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      <WhatsAppButton />
    </main>
  );
}
